import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Chip, FormControl, MenuItem, Select, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import ReactApexChart from "react-apexcharts";
import DnsIcon from "@mui/icons-material/Dns";
import PlaceIcon from "@mui/icons-material/Place";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SpeedIcon from "@mui/icons-material/Speed";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import HubIcon from "@mui/icons-material/Hub";
import ScienceIcon from "@mui/icons-material/Science";
import { colors } from "../../../../theme";
import API from "../../../../api";
import {
  mapAssetRows,
  allVulnerabilitiesForAsset,
  applyFilters,
  vulnSeverityTier,
  severityStyleForTier,
  severityIconForTier,
  worstVulnerability,
  vulnAckKey,
  riskScoreStyle,
  assetIdentity,
  exposureScoreFor,
} from "./assetUtils";
import { resolveClassification, assetTypeLabel, humanizeTaxonomy } from "./assetClassification";
import { zoneRiskStyle, zoneRiskLabel } from "./zoneUtils";
import { computeSecurityScore, computeZoneSecurityScore } from "./securityScore";
import ScoreSimulatorDialog from "./ScoreSimulatorDialog";
import VulnerabilityList, { ExposureChip } from "./VulnerabilityList";

const SEVERITY_TIERS = ["critical", "high", "medium", "low"];
const SEVERITY_RANK = { critical: 4, high: 3, medium: 2, low: 1, none: 0 };

// Fixed-order categorical palette for the breakdown pies, assigned by rank (most-used
// first) — same convention as OtActivityStats.jsx, so every pie in the app reads as
// one consistent visual language instead of MUI's default rainbow palette.
const PALETTE = ["#459095", "#7b5fe0", "#b89400", "#ff9966", "#ffb422", "#ff6b35", "#7b8bff", "#33c2c2"];
const MUTED_COLOR = "#5a5a5a";

// Same reseller/user branch distinction already used by Status/index.jsx and
// network-status/overview/index.jsx.
const ZONES_PATH = window.sessionStorage.getItem("type") === "reseller"
  ? "/reseller/myC/network-status/zones"
  : "/user/network-status/zones";

const withPalette = (series) =>
  series.map((s, i) => ({
    ...s,
    color: (s.id === "other" || s.id === "not-reported" || s.label === "No VLAN") ? MUTED_COLOR : PALETTE[i % PALETTE.length],
  }));

// Long vendor/model names blow up the legend width and squeeze the donut itself —
// truncate for display; the full name is still one hover away via the item tooltip.
const truncateLabel = (label, max = 18) => (label.length > max ? `${label.slice(0, max - 1)}…` : label);

const fmtThroughput = (value) => {
  if (value == null || Number.isNaN(value)) return "-";
  if (value >= 64000) return `${((value * 8) / 1_000_000).toFixed(2)} Gb/s`;
  if (value > 20) return `${((value * 8) / 1000).toFixed(2)} Mb/s`;
  return `${value.toFixed(0)} KB/s`;
};

const fmtPercent = (value) => (value == null || Number.isNaN(value) ? "-" : `${value}%`);
const fmtCount = (value) => (value == null || Number.isNaN(value) ? "-" : value.toLocaleString());
const formatChartTime = (value, timeZone) => new Intl.DateTimeFormat(undefined, {
  timeZone,
  hour: "2-digit",
  minute: "2-digit",
}).format(new Date(value));
const THROUGHPUT_SCALES = {
  kbps: { label: "Kb/s", factor: 8 },
  mbps: { label: "Mb/s", factor: 8 / 1000 },
  gbps: { label: "Gb/s", factor: 8 / 1_000_000 },
};
const TRAFFIC_WINDOWS = [
  { value: "6", label: "6h" },
  { value: "12", label: "12h" },
  { value: "24", label: "24h" },
  { value: "all", label: "48h" },
];

const Tile = ({ icon: Icon, label, value, color = colors.grey[100], onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      flex: "1 1 150px",
      minWidth: 150,
      display: "flex",
      alignItems: "center",
      gap: 1,
      p: 1.2,
      borderRadius: 1,
      backgroundColor: "#1c1c1c",
      border: `1px solid ${colors.grey[800]}`,
      cursor: onClick ? "pointer" : "default",
      "&:hover": onClick ? { borderColor: "#459095" } : undefined,
    }}
  >
    {Icon && <Icon sx={{ color, fontSize: 26, flexShrink: 0 }} />}
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: colors.grey[400], textTransform: "uppercase", letterSpacing: 0.6, whiteSpace: "nowrap" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const SectionTitle = ({ children }) => (
  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#459095", textTransform: "uppercase", letterSpacing: 0.8, mb: 1.8 }}>
    {children}
  </Typography>
);

const OverviewSection = ({ title, subtitle, children, sx }) => (
  <Box sx={{ borderTop: `1px solid ${colors.grey[800]}`, pt: 2.5, ...sx }}>
    <Typography sx={{ fontSize: 14, fontWeight: 700, color: colors.grey[100], mb: 0.35 }}>{title}</Typography>
    <Typography sx={{ fontSize: 11.5, color: colors.grey[500], mb: 1.8 }}>{subtitle}</Typography>
    {children}
  </Box>
);

// Same radii/padding/cornerRadius/highlightScope as OtActivityStats.jsx's MiniPie, so
// every donut in the app reads as one visual language. Legend sits below (wraps across
// rows) instead of to the right, so long labels widen downward instead of squeezing
// the donut itself — labels are pre-truncated by the caller as a second safety net.
const MiniPie = ({ data, onItemClick }) => (
  <PieChart
    series={[{ data, innerRadius: 45, outerRadius: 90, paddingAngle: 2, cornerRadius: 4, highlightScope: { fade: "global", highlight: "item" } }]}
    height={280}
    onItemClick={onItemClick}
    slotProps={{
      legend: {
        direction: "horizontal",
        position: { vertical: "bottom", horizontal: "center" },
        sx: {
          gap: "10px",
          "& .MuiChartsLegend-label": { fontSize: 11, color: colors.grey[300] },
        },
      },
    }}
    tooltip={{ trigger: "item" }}
  />
);

// How much of its own cap each scoring factor is using up right now — a different
// angle from the raw counts in the stat tiles below (this is "how much is it hurting
// the score", not "how many are there"), so the two sections don't just repeat
// each other.
const BreakdownBar = ({ label, penalty, max }) => {
  const pct = max > 0 ? Math.min(100, Math.round((penalty / max) * 100)) : 0;
  const barColor = pct === 0 ? colors.grey[700] : pct < 40 ? "#ffb422" : pct < 75 ? "#ff7744" : "#ff4444";
  return (
    <Box sx={{ minWidth: 130 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.4 }}>
        <Typography sx={{ fontSize: 10.5, color: colors.grey[400], whiteSpace: "nowrap" }}>{label}</Typography>
        <Typography sx={{ fontSize: 10.5, color: colors.grey[500] }}>{pct}%</Typography>
      </Box>
      <Box sx={{ height: 6, borderRadius: 3, backgroundColor: "#161616", overflow: "hidden" }}>
        <Box sx={{ height: "100%", width: `${pct}%`, backgroundColor: barColor, borderRadius: 3 }} />
      </Box>
    </Box>
  );
};

// The letter grade was dropped (score number alone is clearer at a glance); the space
// freed up on the right is filled with the breakdown bars above, not a repeat of the
// stat tiles below.
const ScoreTile = ({ scoreData, onSimulate }) => {
  const { score, color, reasons, breakdown } = scoreData;
  return (
    <Box sx={{ display: "flex", gap: 3, alignItems: "center", p: 2, borderRadius: 1, backgroundColor: "#1c1c1c", border: `1px solid ${colors.grey[800]}`, mb: 2, flexWrap: "wrap" }}>
      <Box
        sx={{
          width: 84, height: 84, borderRadius: "50%", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          border: `4px solid ${color}`, color,
        }}
      >
        <Typography sx={{ fontSize: 30, fontWeight: 800, lineHeight: 1 }}>{score}</Typography>
      </Box>
      <Box sx={{ minWidth: 220, flex: "1 1 220px" }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.grey[100], mb: 0.5 }}>
          Network Security Score
        </Typography>
        {reasons.map((r, i) => (
          <Typography key={i} sx={{ fontSize: 12, color: colors.grey[400] }}>• {r}</Typography>
        ))}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 220, flex: "1 1 220px", ml: { md: "auto" } }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: colors.grey[500], textTransform: "uppercase", letterSpacing: 0.5 }}>
            Score Breakdown
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {breakdown.map((b) => <BreakdownBar key={b.key} {...b} />)}
          </Box>
        </Box>
        <Button
          size="small"
          startIcon={<ScienceIcon sx={{ fontSize: "15px !important" }} />}
          onClick={onSimulate}
          sx={{ color: "#459095", textTransform: "none", fontSize: 11.5, flexShrink: 0, alignSelf: "center" }}
        >
          Simulate
        </Button>
      </Box>
    </Box>
  );
};

// Landing "Overview" tab — Claroty-inspired at-a-glance view of the whole inventory:
// what's in the network, how it's segmented, and how risky it is. Every tile/chart here
// drills down either into a filtered Inventory tab (via onDrillDown) or a specific
// asset's full detail page (via onOpenAsset). Loads its own data independently of
// HostsOnlineGrid — same cost profile already accepted for the whole grid.
//
// `overviewFilters`/`onToggleFilter`: clicking a Type/Vendor/VLAN/Zone breakdown scopes
// this whole Overview to that slice instead of jumping to Inventory. Owned by the parent
// page (not here) since the filter-manage popover and "View in Inventory" button live in
// its title row, not inside this component — `overviewFilters` is a plain object keyed
// by field (so multiple dimensions stack: zone + vlan + ... all AND'd together, but each
// field can only hold one active value at a time).
//
// `orionVisible` (TASK-AI-035): Security/Network segmentation/By Asset Type/By Device
// Class are Orion-derived analysis, gated the same way the rest of the app already gates
// Orion-only surfaces (asset-detail's Security/Connections tabs, the Zones-and-Conduits
// page) — Total Assets/Online and By Vendor/By VLAN are plain inventory facts and stay
// visible regardless.
const AssetsOverview = ({ overviewFilters, onToggleFilter, onDrillDown, onOpenAsset }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [zones, setZones] = useState([]);
  const [acks, setAcks] = useState([]);
  const [loading, setLoading] = useState(true);
  // Network traffic (absorbed from the removed Real Time Transmission page) — a
  // security-relevant network is the priority, so this section renders last.
  const [netTraffic, setNetTraffic] = useState(null);
  const [bpsHistory, setBpsHistory] = useState([]);
  const [simulatorOpen, setSimulatorOpen] = useState(false);
  const [overviewSection, setOverviewSection] = useState("assets");
  const [throughputScale, setThroughputScale] = useState("mbps");
  const [trafficWindow, setTrafficWindow] = useState("all");
  const [orionVisible, setOrionVisible] = useState(true);
  const [exposureByIp, setExposureByIp] = useState({});
  const chartTimezone = window.sessionStorage.getItem("timezone") || "Etc/GMT";

  const loadAcks = () => API.getCveAcks().then((res) => { if (res.data?.success) setAcks(res.data.data || []); });
  const handleAcknowledgeCve = (assetId, cveId, originKey, note) =>
    API.acknowledgeCve({ asset_id: assetId, cve_id: cveId, origin: originKey, note }).then(loadAcks);
  const handleReopenCve = (assetId, cveId, originKey) =>
    API.unacknowledgeCve({ asset_id: assetId, cve_id: cveId, origin: originKey }).then(loadAcks);
  const loadZones = () => API.getZones().then((res) => { if (res.data?.success) setZones(res.data.data || []); });
  // `orion: orionVisible` — senza questo il backend (getRecentHostV2) tratta la
  // richiesta come Orion-disabled e strippa classification/os/risk/cves dalla
  // risposta a prescindere dal reale stato del toggle, lasciando i grafici "By
  // Asset Type"/"By Device Class" vuoti (stesso fix già applicato in asset-detail).
  const loadRows = () => API.getRecentHost({ serials: JSON.stringify([]), all: true, orion: orionVisible }).then((res) => {
    const data = res.data || res;
    if (data?.success) setRows(mapAssetRows(data.data));
  });

  useEffect(() => {
    API.getOrionVisibility()
      .then((res) => setOrionVisible(res?.data?.visibility !== false))
      .catch(() => {});
  }, []);

  // Se Orion viene disabilitato mentre l'utente è sul tab "Security" (ora nascosto),
  // riporta la vista su "Assets" invece di lasciarla bloccata su un tab che non esiste
  // più — stesso principio del redirect di ZonesModuleGuard in App.js.
  useEffect(() => {
    if (!orionVisible && overviewSection === "security") setOverviewSection("assets");
  }, [orionVisible, overviewSection]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadRows(), loadZones(), loadAcks()]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadRows/loadZones/loadAcks are recreated every render (not memoized); adding them here would re-run this effect on every render instead of only when orionVisible changes.
  }, [orionVisible]);

  // Connection count + external-IP reachability per asset, one bulk aggregate
  // for the tenant (zones-ctrl.mjs) — used only to break ties between CVEs of
  // equal severity in the "CVEs Needing Attention" list below, same tie-break
  // as the dashboard's Orion widget. Independent of the main fetch above, so a
  // slow/failed exposure query never blocks the rest of this tab.
  useEffect(() => {
    API.getAssetExposure()
      .then((res) => setExposureByIp(res?.data?.success ? res.data.data || {} : {}))
      .catch((err) => console.error("[AssetsOverview] failed to fetch asset exposure:", err));
  }, []);

  useEffect(() => {
    API.getRealTime()
      .then((result) => setNetTraffic(result.data))
      .catch((error) => console.error("[AssetsOverview] failed to fetch real-time traffic:", error));

    API.getBpsGraphic()
      .then((result) => setBpsHistory(Array.isArray(result.data) ? result.data : []))
      .catch((error) => console.error("[AssetsOverview] failed to fetch traffic history:", error));
  }, []);

  const zoneById = useMemo(() => Object.fromEntries(zones.map((z) => [z._id ?? z.id, z])), [zones]);
  // Fallback for an asset the probe placed in a zone (by name/subnet match)
  // that was never formally assigned via zone_id — same fallback as the Asset
  // Details page's Zone card. Without this, every count/filter/score derived
  // from rowsWithZoneInfo below only ever saw formally-assigned assets, so a
  // network with several zones in active use by the probe but only one or two
  // assets ever manually assigned looked like it had just that one zone.
  const zoneByName = useMemo(
    () => Object.fromEntries(zones.map((z) => [String(z.name ?? "").trim().toLowerCase(), z])),
    [zones]
  );
  const ackSet = useMemo(() => new Set(acks.map((a) => vulnAckKey(a.asset_id, a.cve_id, a.origin))), [acks]);
  const ackMap = useMemo(() => new Map(acks.map((a) => [vulnAckKey(a.asset_id, a.cve_id, a.origin), a])), [acks]);

  // Zone name/risk resolved onto each row (same pattern as HostsOnlineGrid) so
  // applyFilters can match zone_name — the same field name the Inventory grid's own
  // filterModel already uses, kept consistent so "View in Inventory" round-trips cleanly.
  const rowsWithZoneInfo = useMemo(() => rows.map((r) => {
    const probeZoneName = r.zone?.name;
    const zone = zoneById[r.zone_id] || (probeZoneName && zoneByName[String(probeZoneName).trim().toLowerCase()]);
    return { ...r, zone_id: zone ? (zone._id ?? zone.id) : r.zone_id, zone_name: zone?.name ?? "", zone_risk: zone?.risk ?? "none" };
  }), [rows, zoneById, zoneByName]);

  const activeFilterList = useMemo(() => Object.values(overviewFilters || {}), [overviewFilters]);

  const filteredRows = useMemo(
    () => activeFilterList.length > 0
      ? applyFilters(rowsWithZoneInfo, activeFilterList.map(({ field, operator, value }) => ({ field, operator, value })))
      : rowsWithZoneInfo,
    [rowsWithZoneInfo, activeFilterList]
  );

  // Only the zones actually represented in the current slice — otherwise a "Vendor: X"
  // filter would still drag in every zone's risk penalty network-wide.
  const filteredZones = useMemo(() => {
    if (activeFilterList.length === 0) return zones;
    const idsInView = new Set(filteredRows.map((r) => r.zone_id).filter(Boolean));
    return zones.filter((z) => idsInView.has(z._id ?? z.id));
  }, [zones, filteredRows, activeFilterList]);

  const score = useMemo(
    () => computeSecurityScore({ rows: filteredRows, zones: filteredZones, acks }),
    [filteredRows, filteredZones, acks]
  );

  // An empty asset_type means the document never reported one (an old document) —
  // distinct from `unclassified`, which is a real taxonomy answer, so the two get
  // separate slices. Colours come from the app's standard categorical palette; the
  // taxonomy itself is not colour-coded anywhere.
  const byType = useMemo(() => {
    const counts = {};
    filteredRows.forEach((r) => {
      const key = (r.asset_type || "").toLowerCase() || "not-reported";
      counts[key] = (counts[key] || 0) + 1;
    });
    return withPalette(Object.entries(counts).map(([id, value]) => ({
      id,
      value,
      label: id === "not-reported" ? "Not reported" : assetTypeLabel(id),
    })));
  }, [filteredRows]);

  // The most specific classification level each asset reached — a far more useful
  // breakdown than the four families alone once the taxonomy is populated.
  const byClassification = useMemo(() => {
    const counts = {};
    filteredRows.forEach((r) => {
      const k = resolveClassification(r.classification);
      const key = k.type || k.subcategory;
      // Assets that only ever reached asset_type are not "unknown" — the evidence
      // simply stopped there, and lumping them in would overstate what's known.
      if (key) counts[key] = (counts[key] || 0) + 1;
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const top = sorted.slice(0, 7);
    const rest = sorted.slice(7).reduce((sum, [, c]) => sum + c, 0);
    const series = top.map(([label, value]) => ({ id: label, value, label: truncateLabel(humanizeTaxonomy(label)), fullLabel: label }));
    if (rest > 0) series.push({ id: "other", value: rest, label: "Other", fullLabel: "Other" });
    return withPalette(series);
  }, [filteredRows]);

  const byVendor = useMemo(() => {
    const counts = {};
    filteredRows.forEach((r) => {
      const key = r.vendor?.trim() || "Not Found";
      counts[key] = (counts[key] || 0) + 1;
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const top = sorted.slice(0, 6);
    const rest = sorted.slice(6).reduce((sum, [, c]) => sum + c, 0);
    const series = top.map(([label, value], i) => ({ id: i, value, label: truncateLabel(label), fullLabel: label }));
    if (rest > 0) series.push({ id: "other", value: rest, label: "Other", fullLabel: "Other" });
    return withPalette(series);
  }, [filteredRows]);

  const byVlan = useMemo(() => {
    const counts = {};
    filteredRows.forEach((r) => {
      const key = r.vlan != null ? String(r.vlan) : "No VLAN";
      counts[key] = (counts[key] || 0) + 1;
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const top = sorted.slice(0, 6);
    const rest = sorted.slice(6).reduce((sum, [, c]) => sum + c, 0);
    const series = top.map(([label, value], i) => ({ id: i, value, label }));
    if (rest > 0) series.push({ id: "other", value: rest, label: "Other" });
    return withPalette(series);
  }, [filteredRows]);

  // Every configured zone, not just the ones with a matching asset in the
  // current slice — a zone with 0 assets right now (freshly created, or
  // between scans) is still a zone the network is segmented into, and
  // dropping it from this list made "how many zones do I have" undercount
  // whenever a zone wasn't yet represented by any discovered asset.
  // securityScore reuses computeZoneSecurityScore — same weighted formula as the
  // network-wide score (computeSecurityScore above), just scoped to this zone's own
  // assets, so a zone's number and the network number never drift into two
  // separately-tuned heuristics. Sorted riskiest-first (lowest score = most at
  // risk); zones with nothing to score yet (no assets) sort last.
  const byZone = useMemo(() => {
    const counts = {};
    filteredRows.forEach((r) => { if (r.zone_id) counts[r.zone_id] = (counts[r.zone_id] || 0) + 1; });
    return (activeFilterList.length > 0 ? filteredZones : zones)
      .map((zone) => {
        const id = zone._id ?? zone.id;
        return { zone, count: counts[id] || 0, securityScore: computeZoneSecurityScore(id, { rows: filteredRows, zones: [zone], acks }) };
      })
      .sort((a, b) => (a.securityScore?.score ?? 101) - (b.securityScore?.score ?? 101));
  }, [filteredRows, zones, filteredZones, activeFilterList, acks]);

  // byZone is already sorted riskiest-first by the same per-zone score — the top
  // entry that actually has assets in it (and therefore a score) is the answer.
  // An empty zone (declared but nothing deployed there yet) is never "the riskiest"
  // regardless of its manually-assessed risk label.
  const riskiestZone = useMemo(
    () => byZone.find(({ count, securityScore }) => count > 0 && securityScore) ?? null,
    [byZone]
  );

  // Every unacknowledged vulnerability in the current slice, each tagged with its
  // asset — the source for both the severity tiles and the "CVEs needing attention"
  // list below.
  const unackedVulns = useMemo(() => {
    const out = [];
    filteredRows.forEach((row) => {
      allVulnerabilitiesForAsset(row).forEach((v) => {
        if (ackSet.has(vulnAckKey(row.id, v.cve_id, v._originKey))) return;
        out.push({ row, vuln: v, tier: vulnSeverityTier(v) });
      });
    });
    return out.sort((a, b) =>
      SEVERITY_RANK[b.tier] - SEVERITY_RANK[a.tier] || exposureScoreFor(b.row, exposureByIp) - exposureScoreFor(a.row, exposureByIp)
    );
  }, [filteredRows, ackSet, exposureByIp]);

  // Same 10 entries as unackedVulns, reshaped for VulnerabilityList: each vuln
  // tagged with `_assetId` (a flat list spans several assets, unlike the
  // dashboard widget's per-asset groups) and `_row` so renderMeta below can
  // show which asset it's on and how exposed that asset is.
  const unackedVulnsForList = useMemo(
    () => unackedVulns.slice(0, 10).map(({ row, vuln }) => ({ ...vuln, _assetId: row.id, _row: row })),
    [unackedVulns]
  );

  const bySeverity = useMemo(() => {
    const counts = { critical: 0, high: 0, medium: 0, low: 0 };
    unackedVulns.forEach(({ tier }) => {
      if (tier in counts) counts[tier]++;
    });
    return counts;
  }, [unackedVulns]);

  // Tab icon: worst unacked severity present (critical/high only — the Security
  // tab shouldn't flag itself for medium/low, same threshold the rest of the app
  // treats as "needs attention" at a glance).
  const securityTabIcon = useMemo(() => {
    const worstTier = bySeverity.critical > 0 ? "critical" : bySeverity.high > 0 ? "high" : null;
    if (!worstTier) return null;
    const Icon = severityIconForTier(worstTier);
    if (!Icon) return null;
    return { Icon, color: severityStyleForTier(worstTier).color };
  }, [bySeverity]);

  // Ranked by the asset's own risk score first, CVE severity second. The score
  // already folds in role, Purdue proximity and EOL OS — an unpatched PLC with no
  // published CVEs outranks a workstation with a medium one, and ordering by CVE
  // tier alone would put them the other way round.
  // The dynamic score (live telemetry, superset of the static signals) leads
  // whenever the asset has one, same rule as the Risk Score card and the Asset
  // Details summary tile — an asset can look calm on the static score while its
  // dynamic one (which also sees live security events) is what actually earns
  // it a place on this list.
  const topAtRisk = useMemo(() => {
    const primaryRiskScore = (row) => (typeof row.dynamic_risk_score === "number" ? row.dynamic_risk_score : row.risk_score);
    return filteredRows
      .map((row) => {
        const worst = worstVulnerability(allVulnerabilitiesForAsset(row).filter(
          (v) => !ackSet.has(vulnAckKey(row.id, v.cve_id, v._originKey))
        ));
        return { row, worst, tier: worst ? vulnSeverityTier(worst) : "none" };
      })
      .filter((a) => a.tier !== "none" || (typeof primaryRiskScore(a.row) === "number" && primaryRiskScore(a.row) > 0))
      .sort((a, b) =>
        (primaryRiskScore(b.row) ?? -1) - (primaryRiskScore(a.row) ?? -1) ||
        SEVERITY_RANK[b.tier] - SEVERITY_RANK[a.tier]
      )
      .slice(0, 8);
  }, [filteredRows, ackSet]);

  // Packet-type mix (UDP/TCP/ICMP) — same payload the removed Real Time Transmission
  // page's donut used, restyled to match the breakdown pies above.
  const packetTypes = useMemo(() => {
    if (!netTraffic?.series) return [];
    const labels = ["UDP", "TCP", "ICMP"];
    return withPalette(
      netTraffic.series
        .map((v, i) => ({ id: labels[i], value: parseFloat(v) || 0, label: labels[i] }))
        .filter((d) => d.value > 0)
    );
  }, [netTraffic]);

  const visibleBpsHistory = useMemo(() => {
    if (trafficWindow === "all") return bpsHistory;
    const points = Math.max(2, Math.round((bpsHistory.length * Number(trafficWindow)) / 48));
    return bpsHistory.slice(-points);
  }, [bpsHistory, trafficWindow]);

  const trafficChart = useMemo(() => ({
    options: {
      chart: {
        type: "area",
        background: "transparent",
        zoom: { enabled: true, type: "x", autoScaleYaxis: true },
        toolbar: {
          show: true,
          autoSelected: "zoom",
          tools: { download: false, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true },
        },
      },
      colors: ["#459095"],
      dataLabels: { enabled: false },
      stroke: { curve: "straight", width: 2 },
      fill: { type: "gradient", gradient: { opacityFrom: 0.28, opacityTo: 0.02 } },
      markers: { size: 0, hover: { size: 5, sizeOffset: 2 } },
      grid: { borderColor: "#333", strokeDashArray: 3 },
      xaxis: {
        type: "datetime",
        tickAmount: 10,
        labels: { style: { colors: colors.grey[500], fontSize: "10px" }, datetimeUTC: false, formatter: (value) => formatChartTime(Number(value), chartTimezone) },
        axisBorder: { color: "#555" },
        axisTicks: { color: "#555" },
      },
      yaxis: { labels: { style: { colors: colors.grey[500], fontSize: "10px" }, formatter: (value) => `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${THROUGHPUT_SCALES[throughputScale].label}` } },
      tooltip: {
        shared: false,
        intersect: true,
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const value = series[seriesIndex]?.[dataPointIndex];
          const timestamp = w.globals.seriesX[seriesIndex]?.[dataPointIndex];
          const unit = THROUGHPUT_SCALES[throughputScale].label;
          const formattedValue = Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
          const formattedTime = new Intl.DateTimeFormat(undefined, { timeZone: chartTimezone, dateStyle: "short", timeStyle: "medium" }).format(new Date(timestamp));
          return `<div style="padding:8px 10px;color:#eee;background:#1c1c1c;border:1px solid #459095;border-radius:4px;font-size:12px"><div style="color:#999;margin-bottom:4px">${formattedTime}</div><strong style="color:#7de8ed">${formattedValue} ${unit}</strong></div>`;
        },
      },
    },
    series: [{ name: "Throughput", data: visibleBpsHistory.map((h) => [new Date(h.time).getTime(), (h.bps ?? 0) * THROUGHPUT_SCALES[throughputScale].factor]) }],
  }), [visibleBpsHistory, throughputScale, chartTimezone]);

  const online = filteredRows.filter((r) => r.status === "online").length;
  // Worth its own tile: a gateway's observed traffic isn't necessarily its own, so
  // knowing how many there are frames how the rest of the numbers should be read.
  const gateways = filteredRows.filter((r) => r.is_gateway === true).length;

  const filterToInventory = (field, value, operator = "equals") => onDrillDown?.({ field, operator, value });

  if (loading) {
    return <Typography sx={{ color: colors.grey[500], p: 2 }}>Loading overview…</Typography>;
  }

  return (
    <Box sx={{ p: "1rem", display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Tabs value={overviewSection} onChange={(_, value) => setOverviewSection(value)} sx={{ minHeight: 38, borderBottom: `1px solid ${colors.grey[800]}`, "& .MuiTab-root": { minHeight: 38, px: 1.5, fontSize: 11.5, textTransform: "none", color: colors.grey[500] }, "& .Mui-selected": { color: "#7de8ed" } }}>
        <Tab value="assets" label="Assets" />
        {/* Tabs clones its DIRECT children to inject selected/onChange/value --
            wrapping Tab in Tooltip+span (needed to show a tooltip on a disabled
            button) breaks that injection, so the tab never gets marked selected
            and never notifies Tabs of a click. Only apply the wrapper when the
            tab is actually disabled; when enabled, render Tab as a direct child. */}
        {orionVisible
          ? (
            <Tab
              value="security"
              label="Security"
              icon={securityTabIcon ? <securityTabIcon.Icon sx={{ fontSize: 15, color: securityTabIcon.color }} /> : undefined}
              iconPosition="end"
            />
          )
          : (
            <Tooltip title="Orion module is disabled">
              <span>
                <Tab
                  value="security"
                  label="Security"
                  disabled
                  icon={securityTabIcon ? <securityTabIcon.Icon sx={{ fontSize: 15, color: securityTabIcon.color }} /> : undefined}
                  iconPosition="end"
                />
              </span>
            </Tooltip>
          )}
        <Tab value="statistics" label="Statistics" />
      </Tabs>

      {orionVisible && (
        <OverviewSection sx={{ display: overviewSection === "security" ? "block" : "none" }} title="Security posture" subtitle="Risk score and factors that currently affect the network.">
          <ScoreTile scoreData={score} onSimulate={() => setSimulatorOpen(true)} />
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {SEVERITY_TIERS.map((tier) => {
              const c = severityStyleForTier(tier);
              const Icon = severityIconForTier(tier);
              return <Tile key={tier} icon={Icon} label={`Unacked ${c.label}`} value={bySeverity[tier]} color={bySeverity[tier] > 0 ? c.color : colors.grey[500]} onClick={() => filterToInventory("risk_severity", tier)} />;
            })}
          </Box>
        </OverviewSection>
      )}

      <OverviewSection sx={{ display: overviewSection === "assets" ? "block" : "none" }} title="Asset posture" subtitle="What is present in the environment and how it is distributed.">
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Tile icon={DnsIcon} label="Total Assets" value={filteredRows.length} color="#459095" />
          <Tile icon={DnsIcon} label="Online" value={online} color="#4cceac" />
          {orionVisible && (
            <>
              <Tile icon={HubIcon} label="Gateways" value={gateways} color={gateways > 0 ? "#ffb422" : colors.grey[500]} onClick={() => filterToInventory("is_gateway", "1")} />
              <Tile icon={PlaceIcon} label="Zones in Use" value={byZone.filter((z) => z.count > 0).length} color="#459095" onClick={() => filterToInventory("zone_name", "", "notEquals")} />
              <Tile
                icon={PlaceIcon}
                label="Riskiest Zone"
                value={riskiestZone ? riskiestZone.zone.name : "—"}
                color={riskiestZone ? riskiestZone.securityScore.color : colors.grey[500]}
                onClick={riskiestZone ? () => onToggleFilter("zone_name", riskiestZone.zone.name, `Zone: ${riskiestZone.zone.name}`) : undefined}
              />
            </>
          )}
        </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: orionVisible ? "repeat(4, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))" },
          gap: { xs: 2.5, lg: 3 },
          mt: { xs: 1, lg: 2 },
          alignItems: "start",
        }}
      >
        {orionVisible && (
          <Box sx={{ minWidth: 0, pt: 1 }}>
            <SectionTitle>By Asset Type</SectionTitle>
            {byType.length === 0 ? (
              <Typography sx={{ color: colors.grey[600], fontSize: 12.5, fontStyle: "italic" }}>No data.</Typography>
            ) : (
              <MiniPie
                data={byType}
                onItemClick={(_, d) => {
                  const item = byType[d.dataIndex];
                  if (item && item.id !== "not-reported") onToggleFilter("asset_type", item.id, `Type: ${item.label}`);
                }}
              />
            )}
          </Box>
        )}

        {orionVisible && (
          <Box sx={{ minWidth: 0, pt: 1 }}>
            <SectionTitle>By Device Class</SectionTitle>
            {byClassification.length === 0 ? (
              <Typography sx={{ color: colors.grey[600], fontSize: 12.5, fontStyle: "italic" }}>
                No asset has been classified past its family yet.
              </Typography>
            ) : (
              <MiniPie
                data={byClassification}
                onItemClick={(_, d) => {
                  const item = byClassification[d.dataIndex];
                  if (item && item.fullLabel !== "Other") onToggleFilter("classification", item.fullLabel, `Class: ${item.label}`);
                }}
              />
            )}
          </Box>
        )}

        <Box sx={{ minWidth: 0, pt: 1 }}>
          <SectionTitle>By Vendor</SectionTitle>
          {byVendor.length === 0 ? (
            <Typography sx={{ color: colors.grey[600], fontSize: 12.5, fontStyle: "italic" }}>No data.</Typography>
          ) : (
            <MiniPie
              data={byVendor}
              onItemClick={(_, d) => {
                const label = byVendor[d.dataIndex]?.fullLabel;
                if (label && label !== "Other") onToggleFilter("vendor", label, `Vendor: ${label}`);
              }}
            />
          )}
        </Box>

        <Box sx={{ minWidth: 0, pt: 1 }}>
          <SectionTitle>By VLAN</SectionTitle>
          {byVlan.length === 0 ? (
            <Typography sx={{ color: colors.grey[600], fontSize: 12.5, fontStyle: "italic" }}>No data.</Typography>
          ) : (
            <MiniPie
              data={byVlan}
              onItemClick={(_, d) => {
                const label = byVlan[d.dataIndex]?.label;
                if (label && label !== "Other" && label !== "No VLAN") onToggleFilter("vlan", label, `VLAN: ${label}`);
              }}
            />
          )}
        </Box>
      </Box>
      </OverviewSection>

      {orionVisible && (
        <OverviewSection sx={{ display: overviewSection === "assets" ? "block" : "none" }} title="Network segmentation" subtitle="Every configured zone and how many discovered assets sit in each.">
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.2 }}>
            <SectionTitle>By Zone</SectionTitle>
            <Button
              size="small"
              startIcon={<PlaceIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => navigate(ZONES_PATH)}
              sx={{ color: "#459095", textTransform: "none", fontSize: 11.5 }}
            >
              Manage Zones
            </Button>
            </Box>
            {byZone.length === 0 ? (
            <Typography sx={{ color: colors.grey[600], fontSize: 12.5, fontStyle: "italic" }}>No zones configured yet.</Typography>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 1.2 }}>
              {byZone.map(({ zone, count, securityScore }) => {
                const style = zoneRiskStyle(zone.risk);
                const id = zone._id ?? zone.id;
                const active = overviewFilters?.zone_name?.value === zone.name;
                return (
                  <Box
                    key={id}
                    onClick={() => onToggleFilter("zone_name", zone.name, `Zone: ${zone.name}`)}
                    sx={{
                      p: 1.2, borderRadius: 1.5, cursor: "pointer",
                      backgroundColor: "#1c1c1c",
                      border: `1px solid ${active ? "#459095" : colors.grey[800]}`,
                      borderLeft: `3px solid ${style.color}`,
                      "&:hover": { borderColor: "#459095" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.4 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.grey[100] }} noWrap>
                        {zone.name}
                      </Typography>
                      <Chip label={count} size="small" sx={{ bgcolor: style.bg, color: style.color, fontWeight: 700, minWidth: 28, height: 20, fontSize: 10.5 }} />
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: zone.note ? 0.6 : 0 }}>
                      <Chip
                        label={zoneRiskLabel(zone.risk)}
                        size="small"
                        sx={{ bgcolor: style.bg, color: style.color, fontWeight: 700, fontSize: 9.5, height: 18 }}
                      />
                      {securityScore && (
                        <Chip
                          label={`Score ${securityScore.score}`}
                          size="small"
                          sx={{ bgcolor: `${securityScore.color}22`, color: securityScore.color, fontWeight: 700, fontSize: 9.5, height: 18 }}
                        />
                      )}
                    </Box>
                    {zone.note && (
                      <Typography sx={{ fontSize: 11.5, color: colors.grey[400], mt: 0.6 }}>
                        {zone.note}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
            )}
          </Box>
        </OverviewSection>
      )}

      {orionVisible && (
        <OverviewSection sx={{ display: overviewSection === "security" ? "block" : "none" }} title="Security findings" subtitle="Assets and vulnerabilities requiring attention.">
          <Box>
            <SectionTitle>Top At-Risk Assets</SectionTitle>
          {topAtRisk.length === 0 ? (
            <Typography sx={{ color: colors.grey[600], fontSize: 12.5, fontStyle: "italic" }}>
              No unacknowledged vulnerabilities — nothing to show here.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
              {topAtRisk.map(({ row, worst, tier }) => {
                const c = tier !== "none" ? severityStyleForTier(tier) : null;
                const primaryRiskScore = typeof row.dynamic_risk_score === "number" ? row.dynamic_risk_score : row.risk_score;
                const riskStyle = riskScoreStyle(primaryRiskScore);
                const klass = resolveClassification(row.classification);
                const KlassIcon = klass.icon;
                return (
                  <Box
                    key={row.id}
                    onClick={() => onOpenAsset?.(row.id)}
                    sx={{
                      display: "flex", alignItems: "center", gap: 1.2, p: 1, borderRadius: 1,
                      border: `1px solid ${colors.grey[800]}`, cursor: "pointer",
                      "&:hover": { borderColor: "#459095" },
                    }}
                  >
                    {typeof primaryRiskScore === "number" && (
                      <Tooltip title={`Risk score ${primaryRiskScore}/100 — ${riskStyle.label}`}>
                        <Chip
                          label={primaryRiskScore}
                          size="small"
                          sx={{ bgcolor: riskStyle.bg, color: riskStyle.color, fontWeight: 800, fontFamily: "monospace", minWidth: 44 }}
                        />
                      </Tooltip>
                    )}
                    <Tooltip title={klass.path || "Not classified"}>
                      <KlassIcon style={{ fontSize: 18, color: colors.grey[100], flexShrink: 0 }} />
                    </Tooltip>
                    <Typography sx={{ fontFamily: "monospace", fontSize: 12.5, fontWeight: 600, color: colors.grey[100] }}>{row.ip}</Typography>
                    <Typography sx={{ fontSize: 12, color: colors.grey[400] }}>
                      {assetIdentity(row) !== row.ip ? assetIdentity(row) : "—"}
                    </Typography>
                    {c && (
                      <Chip
                        label={c.label}
                        size="small"
                        sx={{ bgcolor: c.bg, color: c.color, fontWeight: 700, ml: "auto" }}
                      />
                    )}
                    <Typography sx={{ fontSize: 11.5, color: colors.grey[500], ml: c ? 0 : "auto", fontFamily: "monospace" }}>
                      {worst?.cve_id ?? ""}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
          </Box>

        <Box sx={{ mt: 2.5 }}>
          <SectionTitle>CVEs Needing Attention</SectionTitle>
        {unackedVulns.length === 0 ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#4cceac" }}>
            <CheckCircleIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: 12.5 }}>Every known CVE in the network has been acknowledged.</Typography>
          </Box>
        ) : (
          <VulnerabilityList
            vulns={unackedVulnsForList}
            ackMap={ackMap}
            onAcknowledge={handleAcknowledgeCve}
            onReopen={handleReopenCve}
            renderMeta={(v) => {
              const row = v._row;
              const exp = exposureByIp[row.ip];
              return (
                <>
                  <ExposureChip row={row} exposureByIp={exposureByIp} />
                  {exp?.hasExternalConnections && (
                    <Tooltip title="This asset has connections reaching an external IP">
                      <Chip label="External reach" size="small" sx={{ bgcolor: "#ff774422", color: "#ff7744", fontWeight: 700, fontSize: 9.5, height: 18 }} />
                    </Tooltip>
                  )}
                  <Tooltip title="Open asset details">
                    <Typography
                      onClick={(e) => { e.stopPropagation(); onOpenAsset?.(row.id); }}
                      sx={{ fontSize: 12, color: "#7de8ed", cursor: "pointer", textDecoration: "underline" }}
                    >
                      {row.ip} · {assetIdentity(row) !== row.ip ? assetIdentity(row) : "—"}
                    </Typography>
                  </Tooltip>
                </>
              );
            }}
          />
        )}
        </Box>
      </OverviewSection>
      )}

      <OverviewSection sx={{ display: overviewSection === "statistics" ? "block" : "none" }} title="Network Statistics" subtitle="Current traffic health and recent throughput.">
        <Box>
          <SectionTitle>Network Traffic</SectionTitle>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          <Tile icon={SpeedIcon} label="Throughput" value={fmtThroughput(netTraffic?.bps)} color="#459095" />
          <Tile icon={SwapVertIcon} label="Packets/s" value={fmtCount(netTraffic?.pps)} color="#459095" />
          <Tile
            icon={WarningAmberIcon}
            label="Network Error"
            value={fmtPercent(netTraffic?.networkError)}
            color={netTraffic?.networkError > 0 ? "#ff6644" : colors.grey[500]}
          />
          <Tile
            icon={GraphicEqIcon}
            label="Network Noise"
            value={fmtPercent(netTraffic?.networkNoise)}
            color={netTraffic?.networkNoise > 10 ? "#ffa726" : colors.grey[500]}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap", mt: 4 }}>
          <Box sx={{ flex: "2 1 420px", minWidth: 320 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 2, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 11.5, color: colors.grey[500] }}>Throughput over the last 48h</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                <FormControl size="small" sx={{ minWidth: 92 }}>
                  <Select value={throughputScale} onChange={(event) => setThroughputScale(event.target.value)} sx={{ height: 28, fontSize: 11.5, color: colors.grey[300] }}>
                    {Object.entries(THROUGHPUT_SCALES).map(([key, scale]) => <MenuItem key={key} value={key} sx={{ fontSize: 11.5 }}>{scale.label}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 76 }}>
                  <Select value={trafficWindow} onChange={(event) => setTrafficWindow(event.target.value)} sx={{ height: 28, fontSize: 11.5, color: colors.grey[300] }}>
                    {TRAFFIC_WINDOWS.map((window) => <MenuItem key={window.value} value={window.value} sx={{ fontSize: 11.5 }}>{window.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            {visibleBpsHistory.length === 0 ? (
              <Typography sx={{ color: colors.grey[600], fontSize: 12.5, fontStyle: "italic", textAlign: "center", py: 4 }}>
                No traffic data available.
              </Typography>
            ) : (
              <ReactApexChart options={trafficChart.options} series={trafficChart.series} type="area" height={300} />
            )}
          </Box>

          <Box sx={{ flex: "1 1 260px", minWidth: 240 }}>
            <Typography sx={{ fontSize: 11.5, color: colors.grey[500], textAlign: "center", mb: 1 }}>
              Packet Types
            </Typography>
            {packetTypes.length === 0 ? (
              <Typography sx={{ color: colors.grey[600], fontSize: 12.5, fontStyle: "italic", textAlign: "center", py: 4 }}>
                No packet data available.
              </Typography>
            ) : (
              <MiniPie data={packetTypes} />
            )}
          </Box>
        </Box>
        </Box>
      </OverviewSection>

      <ScoreSimulatorDialog
        open={simulatorOpen}
        onClose={() => setSimulatorOpen(false)}
        rows={filteredRows}
        zones={filteredZones}
        acks={acks}
        currentScore={score}
        scopeLabel={activeFilterList.length > 0 ? activeFilterList.map((f) => f.label).join(" + ") : "Whole network"}
      />
    </Box>
  );
};

export default AssetsOverview;
